import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/cliente';
import type { Opcion, Pagina } from '../api/tipos';
import { Cargando, Error } from '../componentes/comunes';
import { useSesion } from '../sesion';

/**
 * Reporte de cobranza: qué facturas pagó el cliente y cuándo.
 *
 * Es el espejo del de facturado. Aquél dice lo que se emitió; éste lo que
 * entró, y el corte es por **fecha de depósito**: una factura de agosto
 * cobrada en septiembre es cobranza de septiembre aunque sea facturación
 * de agosto. Por eso son dos reportes y no dos columnas del mismo.
 *
 * Las columnas son las diez que dictó Karin, idénticas a las del otro
 * reporte a propósito: quien recibe los dos archivos los pega y los
 * compara con las mismas fórmulas.
 *
 * Los totales salen de la API y no de sumar lo que llegó: el detalle
 * viene acotado a cien renglones y sumarlo daría una cifra más chica que
 * la verdadera — la peor clase de error, porque parece correcta.
 */

interface Totales {
  cuantas: number;
  subtotal: number;
  iva: number;
  total: number;
  aplicado: number;
}

interface Renglon {
  fechaPago: string;
  folio: string;
  uuid: string | null;
  cliente: string;
  sucursal: string | null;
  poNumero: string;
  subtotal: number;
  iva: number;
  total: number;
  metodoPago: string | null;
  aplicado: number;
  referencia: string | null;
  /** La zona de la sucursal de la factura. Nula en una libre. */
  zona: string | null;
}

interface Reporte {
  totales: Totales;
  detalle: Renglon[];
  detalleCompleto: boolean;
}

/*
 * El método del SAT —PUE, PPD— salió de este reporte junto con el UUID, la
 * sucursal y la orden de compra: Karin dejó las nueve columnas que sirven
 * para cuadrar un depósito contra el estado de cuenta, y el método sirve
 * para la otra pregunta. Sigue en el reporte de Facturado, que es donde
 * se mira qué se emitió.
 *
 * El campo viaja igual en la respuesta de la API, sin pintarse: quitarlo
 * de allá obligaría a tocar el tipo compartido por una columna que puede
 * volver.
 */

function moneda(valor: number): string {
  return valor.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

function fecha(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
}

export default function ReporteCobranza() {
  const { puede } = useSesion();
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [clientes, setClientes] = useState<Opcion[]>([]);
  const [reporte, setReporte] = useState<Reporte | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api.get<Pagina<Opcion>>('/clientes', { limite: 200, estado: 'activo' })
      .then((r) => setClientes(r.data))
      .catch(() => undefined);
  }, []);

  const parametros = useCallback(() => ({
    desde: desde || undefined,
    hasta: hasta || undefined,
    clienteId: clienteId || undefined,
  }), [desde, hasta, clienteId]);

  useEffect(() => {
    setCargando(true);
    setError(null);
    api.get<Reporte>('/facturacion/reporte/cobranza', parametros())
      .then(setReporte)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [parametros]);

  return (
    <>
      <div className="panel">
        <p className="sub">
          Lo que el cliente ya pagó, por fecha de depósito. Una factura de
          agosto cobrada en septiembre cuenta en septiembre.
        </p>

        <div className="rejilla-campos">
          <div className="campo">
            <label htmlFor="cob-desde">Desde</label>
            <input
              id="cob-desde" type="date" value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
          </div>
          <div className="campo">
            <label htmlFor="cob-hasta">Hasta</label>
            <input
              id="cob-hasta" type="date" value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            />
            <span className="ayuda">Vacío llega hasta hoy. Los dos extremos cuentan.</span>
          </div>
          <div className="campo">
            <label htmlFor="cob-cliente">Cliente</label>
            <select
              id="cob-cliente" value={clienteId}
              onChange={(e) => setClienteId(e.target.value)}
            >
              <option value="">Todos</option>
              {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
        </div>

        {(puede('facturacion', 'exportar') || puede('contabilidad', 'exportar')) && (
          <div className="controles" style={{ marginTop: 12 }}>
            <button
              onClick={() => {
                void api.descargar('/facturacion/reporte/cobranza/exportar', parametros());
              }}
            >
              Descargar Excel
            </button>
            <span className="conteo">
              El archivo lleva todos los renglones y los filtros escritos dentro.
            </span>
          </div>
        )}
      </div>

      <Error error={error} />

      {cargando && !reporte ? <Cargando /> : reporte && (
        <>
          <div className="rejilla-catalogos">
            <Tarjeta titulo="Facturas cobradas" valor={String(reporte.totales.cuantas)} />
            <Tarjeta titulo="Subtotal" valor={moneda(reporte.totales.subtotal)} />
            <Tarjeta titulo="I.V.A." valor={moneda(reporte.totales.iva)} />
            <Tarjeta titulo="Total" valor={moneda(reporte.totales.total)} />
          </div>

          {/* Lo aplicado distinto del total significa que algún depósito
              cubrió sólo una parte de su factura. Callarlo dejaría creer
              que entró el total de la columna. */}
          {Math.round((reporte.totales.total - reporte.totales.aplicado) * 100) !== 0 && (
            <div className="aviso" style={{ marginTop: 12 }}>
              De esas facturas entraron <strong>{moneda(reporte.totales.aplicado)}</strong>:
              alguna se cobró en parte. Los renglones con cobro parcial van
              marcados en el Excel.
            </div>
          )}

          <div className="panel">
            <h2>Detalle</h2>
            {!reporte.detalleCompleto && (
              <p className="sub">
                Se muestran los primeros 100. El Excel los lleva todos.
              </p>
            )}
            {reporte.detalle.length === 0 ? (
              <div className="vacio">
                No hay ninguna factura cobrada en ese periodo.
              </div>
            ) : (
              <table>
                {/* Las columnas son las del Excel, en el mismo orden.

                    Karin: «en reporte cobranza al generar el reporte
                    necesito estas columnas - F. PAGO, No. TRANS.,
                    FACTURA, CLIENTE, SUBTOTAL, I.V.A., TOTAL, PAGADO,
                    ZONA». Dejan de ser las de Facturado a propósito: este
                    reporte contesta «qué entró y por qué transferencia» y
                    aquél «qué se emitió». Para cuadrar un depósito contra
                    el estado de cuenta lo que hace falta es el número de
                    la transferencia y la zona, no el UUID ni el método. */}
                <thead>
                  <tr>
                    <th>F. pago</th>
                    <th>No. trans.</th>
                    <th>Factura</th>
                    <th>Cliente</th>
                    <th className="numero">Subtotal</th>
                    <th className="numero">I.V.A.</th>
                    <th className="numero">Total</th>
                    <th className="numero">Pagado</th>
                    <th>Zona</th>
                  </tr>
                </thead>
                <tbody>
                  {reporte.detalle.map((r) => {
                    const parcial = Math.round((r.total - r.aplicado) * 100) !== 0;

                    return (
                      <tr key={`${r.folio}-${r.fechaPago}`}>
                        <td>{fecha(r.fechaPago)}</td>
                        {/* La referencia del banco: es por donde se casa el
                            depósito contra el estado de cuenta. */}
                        <td className="mono">{r.referencia ?? '—'}</td>
                        <td className="mono">{r.folio}</td>
                        <td>{r.cliente}</td>
                        <td className="numero">{moneda(r.subtotal)}</td>
                        <td className="numero">{moneda(r.iva)}</td>
                        <td className="numero">{moneda(r.total)}</td>
                        {/* Lo pagado por debajo del total es un cobro
                            parcial, y se ve sin tener que restar. */}
                        <td
                          className="numero"
                          style={parcial ? { color: '#b02a37', fontWeight: 600 } : undefined}
                        >
                          {moneda(r.aplicado)}
                        </td>
                        <td>{r.zona ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </>
  );
}

function Tarjeta({ titulo, valor }: { titulo: string; valor: string }) {
  return (
    <div className="tarjeta" style={{ cursor: 'default' }}>
      <div className="conteo">{titulo}</div>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{valor}</div>
    </div>
  );
}
